import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Card, CardHeader, Grid, Button, Stack, Box, CircularProgress } from '@material-ui/core';
import Alert from '@mui/material/Alert';
import LifecycleEditor, {
  buildLifecyclePayload,
  getLifecycleValues
} from '../../lifecycle/LifecycleEditor';
import { setOpenSnackbar } from '../../../store/slices/snackbarSlice';
import useVrackApi from '../../../hooks/useVrackApi';

export default function UpdateDefaultRackLifeCycle() {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [values, setValues] = useState(getLifecycleValues());
  const [savedValues, setSavedValues] = useState();

  const dispatch = useDispatch();
  const openSnackbar = (payload) => dispatch(setOpenSnackbar(payload));

  // Initialize useVrackApi hooks for lifecycle-defaults endpoints
  const { fetchData: getLifecycleDefaults } = useVrackApi(
    'get',
    '/admin/features/lifecycle-defaults'
  );
  const { fetchData: updateLifecycleDefaults } = useVrackApi(
    'post',
    '/admin/features/lifecycle-defaults'
  );

  // Disable Save when an enabled policy has an empty window-days field
  const hasEmptyWindowDays =
    (values.shutdownEnabled && values.shutdownWindowDays === '') ||
    (values.deletionEnabled && values.deletionWindowDays === '');

  // Fetch current lifecycle defaults from the API on mount.
  useEffect(() => {
    getLifecycleDefaults()
      .then((data) => {
        const fetched = getLifecycleValues(data);
        setValues(fetched);
        setSavedValues(fetched);
      })
      .catch((err) => {
        openSnackbar({
          message: err.response?.data?.message || 'Failed to load lifecycle defaults',
          severity: 'error'
        });
        setIsError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setValues(savedValues);
    setIsEditing(false);
  };

  const handleSave = () => {
    updateLifecycleDefaults({ data: buildLifecyclePayload(values) })
      .then(() => {
        openSnackbar({ message: 'Updated default rack lifecycle successfully!' });
        setSavedValues(values);
        setIsEditing(false);
      })
      .catch((err) => {
        openSnackbar({
          message: err.response?.data?.message || 'Failed to update lifecycle defaults',
          severity: 'error'
        });
      });
  };

  return (
    <Card>
      <CardHeader
        title="Default Rack Lifecycle"
        subheader="Configure the default lifecycle policy for racks without explicit overrides."
      />
      <Grid container p={3} pr={5} rowSpacing={3}>
        {isLoading ? (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          </Grid>
        ) : (
          <>
            {isError ? (
              <Grid item xs={12}>
                <Alert severity="error">Failed to load default lifecycle values.</Alert>
              </Grid>
            ) : (
              <>
                <Grid item xs={12}>
                  <LifecycleEditor values={values} onChange={setValues} isEditing={isEditing} />
                </Grid>

                <Grid item xs={12}>
                  <Stack direction="row" spacing={1}>
                    {!isEditing ? (
                      <Button variant="contained" onClick={handleEdit}>
                        Edit
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleSave}
                          disabled={hasEmptyWindowDays}
                        >
                          Save
                        </Button>
                        <Button variant="outlined" onClick={handleCancel}>
                          Cancel
                        </Button>
                      </>
                    )}
                  </Stack>
                </Grid>
              </>
            )}
          </>
        )}
      </Grid>
    </Card>
  );
}
